import {
  forwardRef,
  Ref,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Whisper, Popover } from "rsuite";

import { FileInterface } from "../../types/RecordInterfaces";
import EditingPopup from "./Attachments/EditingPopup";
import { FileModal } from "../Modals/FileModal";
import useToggle from "../../hooks/useToggle";

const noImageStyles: any = (inputSize: "sm" | "md", disabled: boolean) => {
  return {
    height:
      inputSize === "sm"
        ? "calc( 0.45em + 1.25rem + 5px)"
        : "calc( 1.5em + 1.25rem + 5px)",
    borderRadius: "0.25rem",
    fontSize: "0.875rem",
    lineHeight: 1.5,
    padding: inputSize === "sm" ? "0.25rem 0.5rem" : "0.625rem 0.75rem",
    overflowY: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    background: disabled ? "#c6cac7" : "unset",
    cursor: disabled ? "not-allowed" : "pointer",
  };
};

const imageStyles = {
  textAlign: "center",
  cursor: "pointer",
  width: "100%",
  borderRadius: "0.25rem",
};

interface MediaCreateProps {
  inputSize?: "sm" | "md";
  onChange: (data: FileInterface | null) => void;
  field: any;
  existingValue?: FileInterface | null;
  type: string;
  disabled: boolean;
}

const emptyArray: any[] = [];

const MediaCreate = (
  {
    inputSize = "sm",
    onChange,
    field,
    existingValue,
    type,
    disabled,
  }: MediaCreateProps,
  ref: Ref<HTMLInputElement>
) => {
  const whisperRef = useRef<any>();

  const [mediaData, setData] = useState(
    existingValue ? [existingValue] : emptyArray
  );
  const [modalOpen, toggleOpen] = useToggle(false);
  const [file, setFile] = useState<FileInterface | null>(null);
  const [keepOpen, setKeepOpen] = useState(false);

  // Monitor existingValue. If it's empty, clear the file component.
  useEffect(() => {
    if (existingValue) return setData([existingValue]);

    if (!existingValue) setData([]);
  }, [existingValue]);

  const onDelete = () => {
    setData([]);
    onChange(null);
  };

  const afterUpload = (res: FileInterface) => {
    setData([res]);
    onChange(res);
  };

  const options = [
    {
      name: `Remove ${type}`,
      icon: "mdi-delete",
      action: () => {
        setData([]);
        onChange(null);
      },
    },
  ];

  // @ts-ignore
  useImperativeHandle(ref, () => ({
    focus: () => {
      whisperRef?.current?.open();
    },
  }));

  return (
    <>
      <Whisper
        trigger={!keepOpen ? "click" : "none"}
        ref={whisperRef}
        placement="auto"
        speaker={
          <Popover
            className="sidebar-popover p-0 m-0"
            style={{ width: "350px" }}
          >
            <EditingPopup
              field={field}
              fileData={mediaData}
              type={type as any}
              closeRecord={() => whisperRef.current.close()}
              afterUpload={afterUpload}
              titleOptions={`${type} Options`}
              setData={setData}
              options={options}
              setFile={setFile}
              onDelete={onDelete}
              openModal={toggleOpen}
              setKeepOpen={setKeepOpen}
              multiFiles={false}
            />
          </Popover>
        }
      >
        <div
          style={
            mediaData[0]?.File
              ? imageStyles
              : noImageStyles(inputSize, disabled)
          }
          className={`d-flex justify-content-between text-sm align-items-center border ${
            mediaData[0]?.File ? "p-1" : ""
          }`}
        >
          <span>{mediaData[0]?.Name ? mediaData[0].Name : "--"}</span>
        </div>
      </Whisper>
      {file && (
        <FileModal
          isOpen={modalOpen}
          file={file}
          closeModal={() => {
            toggleOpen();
            setFile(null);
          }}
        />
      )}
    </>
  );
};

export default forwardRef(MediaCreate);
